<template>
  <head>
    <meta charset="utf-8" />
    <title>CBHI | Edit Member</title>
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <meta content="Edit member information" name="description" />
    <meta content="Kelvin Njuguna Ndungu" name="author" />
    <!-- App favicon -->
    <link rel="shortcut icon" href="assets/images/favicon.svg">

    <!-- Bootstrap Css -->
    <link href="assets/css/bootstrap.min.css" id="bootstrap-style" rel="stylesheet" type="text/css" />
    <!-- Icons Css -->
    <link href="assets/css/icons.min.css" rel="stylesheet" type="text/css" />

    <!-- Bootstrap select styling -->
    <link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/bootstrap-select@1.13.14/dist/css/bootstrap-select.min.css">

    <!-- App Css-->
    <link href="assets/css/app.min.css" id="app-style" rel="stylesheet" type="text/css" />
    <!-- custom Css-->
    <link href="assets/css/custom.css" id="app-style" rel="stylesheet" type="text/css" />
  </head>

  <body data-sidebar="dark">
  <!-- Begin page -->
  <div id="layout-wrapper">
    <!-- Navigation Bar -->
    <NavigationBar />

    <!-- Main Content -->
    <div class="main-content">
      <div class="page-content">
        <div class="container-fluid">
          <!-- Page Title -->
          <div class="row">
            <div class="col-12">
              <div class="page-title-box d-sm-flex align-items-center justify-content-between">
                <h4 class="mb-sm-0 font-size-18">Edit Member Information</h4>
                <div class="page-title-right">
                  <ol class="breadcrumb m-0">
                    <li class="breadcrumb-item"><a href="/members">Members</a></li>
                    <li class="breadcrumb-item active">Edit Member</li>
                  </ol>
                </div>
              </div>
            </div>
          </div>

          <!-- Loading State -->
          <div v-if="initialLoading" class="row justify-content-center">
            <div class="col-12">
              <div class="card">
                <div class="card-body">
                  <div class="text-center py-5">
                    <div class="spinner-border text-primary" role="status">
                      <span class="visually-hidden">Loading...</span>
                    </div>
                    <h5 class="mt-3">Loading member information...</h5>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Edit Form -->
          <div v-else class="row">
            <div class="col-12">
              <div class="card">
                <!-- Card Header -->
                <div class="card-header bg-white">
                  <div class="d-flex justify-content-between align-items-center">
                    <div>
                      <h4 class="card-title mb-1">Edit Member Information</h4>
                      <p class="text-muted mb-0">Update member details and beneficiaries</p>
                    </div>
                    <div>
                      <span v-if="memberForm.cbhiNumber" class="badge bg-primary px-3 py-2">{{ memberForm.cbhiNumber }}</span>
                      <span v-if="memberForm.status" class="badge px-3 py-2 ms-2" :class="statusBadgeClass">
                          {{ memberForm.status }}
                        </span>
                    </div>
                  </div>
                </div>

                <!-- Card Body -->
                <div class="card-body">
                  <!-- Alerts -->
                  <div v-if="alert.show" class="alert alert-dismissible fade show" :class="alertClass" role="alert">
                    {{ alert.message }}
                    <button type="button" class="btn-close" @click="clearAlert"></button>
                  </div>

                  <!-- Member Status Info -->
                  <div v-if="memberStatus && memberStatus.totalBeneficiaries !== undefined" class="row mb-4">
                    <div class="col-12">
                      <div class="alert alert-info bg-info bg-opacity-10 border-0">
                        <div class="row">
                          <div class="col-md-3">
                            <strong>Membership Status:</strong>
                            <span :class="memberStatus.isActive ? 'text-success ms-2' : 'text-danger ms-2'">
                                <i :class="memberStatus.isActive ? 'bi bi-check-circle' : 'bi bi-x-circle'"></i>
                                {{ memberStatus.isActive ? 'Active' : 'Inactive' }}
                              </span>
                          </div>
                          <div class="col-md-3">
                            <strong>Days Until Expiry:</strong>
                            <span class="ms-2">{{ memberStatus.daysUntilExpiry || 'N/A' }}</span>
                          </div>
                          <div class="col-md-3">
                            <strong>Total Beneficiaries:</strong>
                            <span class="ms-2">{{ memberStatus.totalBeneficiaries || 0 }}</span>
                          </div>
                          <div class="col-md-3">
                            <strong>Total Premium Paid:</strong>
                            <span class="ms-2">ETB {{ formatCurrency(memberStatus.totalPaidPremiums || 0) }}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- General Information Section -->
                  <div class="section-header mb-4" style="background-color: #e7f3ff; border-left-color: #0d6efd;">
                    <h5 class="mb-0 text-primary">
                      <i class="bi bi-person-circle me-2"></i>
                      General Information (Primary Beneficiary)
                    </h5>
                  </div>

                  <!-- Primary Member Fields -->
                  <div class="row g-3 mb-4">
                    <div class="col-md-2">
                      <label class="form-label">Title</label>
                      <select v-model="memberForm.title" class="form-select">
                        <option value="">Select Title</option>
                        <option value="Mr">Mr</option>
                        <option value="Mrs">Mrs</option>
                        <option value="Ms">Ms</option>
                        <option value="Miss">Miss</option>
                        <option value="Dr">Dr</option>
                        <option value="Prof">Prof</option>
                        <option value="Mx">Mx</option>
                      </select>
                    </div>
                    <div class="col-md-7">
                      <label class="form-label">Full Name <span class="text-danger">*</span></label>
                      <input
                          v-model="memberForm.fullName"
                          type="text"
                          class="form-control"
                          :class="{ 'is-invalid': errors.fullName }"
                          placeholder="Enter full name (e.g., Abebe Bikila Kebede)"
                          required
                      >
                      <div v-if="errors.fullName" class="invalid-feedback">
                        {{ errors.fullName }}
                      </div>
                      <small class="text-muted">
                        <i class="bi bi-info-circle"></i> Enter complete Ethiopian name
                      </small>
                    </div>
                    <div class="col-md-3">
                      <label class="form-label">Gender <span class="text-danger">*</span></label>
                      <select v-model="memberForm.gender" class="form-select" :class="{ 'is-invalid': errors.gender }" required>
                        <option value="">Select Gender</option>
                        <option value="male">Male</option>
                        <option value="female">Female</option>
                      </select>
                      <div v-if="errors.gender" class="invalid-feedback">
                        {{ errors.gender }}
                      </div>
                    </div>
                  </div>

                  <div class="row g-3 mb-4">
                    <div class="col-md-4">
                      <label class="form-label">Grandfather's Name</label>
                      <input
                          v-model="memberForm.grandfatherName"
                          type="text"
                          class="form-control"
                          placeholder="Grandfather's name"
                      >
                    </div>
                    <div class="col-md-4">
                      <label class="form-label">Date of Birth <span class="text-danger">*</span></label>
                      <input
                          v-model="memberForm.dateOfBirth"
                          type="date"
                          class="form-control"
                          :class="{ 'is-invalid': errors.dateOfBirth }"
                          :max="maxDate"
                          required
                      >
                      <div v-if="errors.dateOfBirth" class="invalid-feedback">
                        {{ errors.dateOfBirth }}
                      </div>
                      <small v-if="memberForm.dateOfBirth" class="text-muted d-block mt-1">
                        Age: {{ calculateAge(memberForm.dateOfBirth) }} years
                      </small>
                    </div>
                    <div class="col-md-4">
                      <label class="form-label">National ID</label>
                      <input
                          v-model="memberForm.nationalId"
                          type="text"
                          class="form-control"
                          placeholder="Enter national ID (optional)"
                      >
                      <small class="text-muted">
                        <i class="bi bi-info-circle"></i> Optional field
                      </small>
                    </div>
                  </div>

                  <div class="row g-3 mb-4">
                    <div class="col-md-4">
                      <label class="form-label">Marital Status</label>
                      <select v-model="memberForm.maritalStatus" class="form-select">
                        <option value="">Select Status</option>
                        <option value="single">Single</option>
                        <option value="married">Married</option>
                        <option value="divorced">Divorced</option>
                        <option value="widowed">Widowed</option>
                      </select>
                    </div>
                    <div class="col-md-4">
                      <label class="form-label">Occupation</label>
                      <input
                          v-model="memberForm.occupation"
                          type="text"
                          class="form-control"
                          placeholder="Enter occupation (optional)"
                      >
                    </div>
                    <div class="col-md-4">
                      <label class="form-label">Monthly Income (ETB)</label>
                      <input
                          v-model="memberForm.monthlyIncome"
                          type="number"
                          class="form-control"
                          placeholder="0"
                          min="0"
                      >
                    </div>
                  </div>

                  <hr class="my-4">

                  <!-- Contact Information Section -->
                  <div class="section-header mb-4" style="background-color: #e7f3ff; border-left-color: #0d6efd;">
                    <h5 class="mb-0 text-primary">
                      <i class="bi bi-telephone me-2"></i>
                      Contact Information
                    </h5>
                  </div>

                  <div class="row g-3 mb-4">
                    <div class="col-md-6">
                      <label for="phone" class="form-label">
                        Phone Number
                      </label>
                      <div class="input-group">
                        <span class="input-group-text"><i class="bi bi-telephone"></i></span>
                        <input
                            id="phone"
                            v-model="memberForm.phoneNumber"
                            type="tel"
                            class="form-control"
                            placeholder="Enter phone number (optional)"
                        >
                      </div>
                      <small class="text-muted">
                        <i class="bi bi-info-circle"></i> Optional field
                      </small>
                    </div>

                    <div class="col-md-6">
                      <label for="email" class="form-label">Email Address</label>
                      <div class="input-group">
                        <span class="input-group-text"><i class="bi bi-envelope"></i></span>
                        <input
                            id="email"
                            v-model="memberForm.emailAddress"
                            type="email"
                            class="form-control"
                            :class="{ 'is-invalid': errors.emailAddress }"
                            placeholder="email@example.com (optional)"
                        >
                      </div>
                      <div v-if="errors.emailAddress" class="invalid-feedback d-block">
                        {{ errors.emailAddress }}
                      </div>
                    </div>
                  </div>

                  <div class="row g-3 mb-4">
                    <div class="col-md-12">
                      <label for="address" class="form-label">
                        Address <span class="text-danger">*</span>
                      </label>
                      <div class="input-group">
                        <span class="input-group-text"><i class="bi bi-geo-alt"></i></span>
                        <input
                            id="address"
                            v-model="memberForm.address"
                            type="text"
                            class="form-control"
                            :class="{ 'is-invalid': errors.address }"
                            placeholder="Enter address"
                            required
                        >
                      </div>
                      <div v-if="errors.address" class="invalid-feedback d-block">
                        {{ errors.address }}
                      </div>
                    </div>
                  </div>

                  <!-- Location Information -->
                  <div class="section-header mb-4" style="background-color: #e7f3ff; border-left-color: #0d6efd;">
                    <h5 class="mb-0 text-primary">
                      <i class="bi bi-geo-alt me-2"></i>
                      Location Information
                    </h5>
                  </div>

                  <!-- Location validation alert -->
                  <div v-if="locationValidationError" class="alert alert-warning mb-3">
                    <i class="bi bi-exclamation-triangle me-2"></i>
                    All location fields (Region, Zone, Woreda, and Kebele) must be selected.
                  </div>

                  <div class="row g-3 mb-4">
                    <div class="col-md-3">
                      <label class="form-label">Region <span class="text-danger">*</span></label>
                      <select
                          v-model="memberForm.regionId"
                          @change="onRegionChange"
                          class="form-select form-control"
                          :class="{ 'is-invalid': errors.regionId }"
                          required
                      >
                        <option value="">Select Region</option>
                        <option v-for="region in regions" :key="region.region_id" :value="region.region_id">
                          {{ region.region_name }}
                        </option>
                      </select>
                      <div v-if="errors.regionId" class="invalid-feedback">
                        {{ errors.regionId }}
                      </div>
                    </div>
                    <div class="col-md-3">
                      <label class="form-label">Zone <span class="text-danger">*</span></label>
                      <select
                          v-model="memberForm.zoneId"
                          @change="onZoneChange"
                          class="form-select form-control"
                          :class="{ 'is-invalid': errors.zoneId }"
                          :disabled="!memberForm.regionId"
                          required
                      >
                        <option value="">Select Zone</option>
                        <option v-for="zone in zones" :key="zone.zone_id" :value="zone.zone_id">
                          {{ zone.zone_name }}
                        </option>
                      </select>
                      <div v-if="errors.zoneId" class="invalid-feedback">
                        {{ errors.zoneId }}
                      </div>
                    </div>
                    <div class="col-md-3">
                      <label class="form-label">Woreda <span class="text-danger">*</span></label>
                      <select
                          v-model="memberForm.woredaId"
                          @change="onWoredaChange"
                          class="form-select form-control"
                          :class="{ 'is-invalid': errors.woredaId }"
                          :disabled="!memberForm.zoneId"
                          required
                      >
                        <option value="">Select Woreda</option>
                        <option v-for="woreda in woredas" :key="woreda.woreda_id" :value="woreda.woreda_id">
                          {{ woreda.woreda_name }}
                        </option>
                      </select>
                      <div v-if="errors.woredaId" class="invalid-feedback">
                        {{ errors.woredaId }}
                      </div>
                    </div>
                    <div class="col-md-3">
                      <label class="form-label">Kebele <span class="text-danger">*</span></label>
                      <select
                          v-model="memberForm.kebeleId"
                          class="form-select form-control"
                          :class="{ 'is-invalid': errors.kebeleId }"
                          :disabled="!memberForm.woredaId"
                          required
                      >
                        <option value="">Select Kebele</option>
                        <option v-for="kebele in kebeles" :key="kebele.kebele_id" :value="kebele.kebele_id">
                          {{ kebele.kebele_name }}
                        </option>
                      </select>
                      <div v-if="errors.kebeleId" class="invalid-feedback">
                        {{ errors.kebeleId }}
                      </div>
                    </div>
                  </div>

                  <hr class="my-4">
                </div>

                <!-- Card Footer -->
                <div class="card-footer bg-white">
                  <div class="d-flex justify-content-between align-items-center">
                    <div>
                      <button
                          @click="updateMember"
                          type="button"
                          class="btn btn-primary me-2"
                          :disabled="loading"
                      >
                        <span v-if="loading" class="spinner-border spinner-border-sm me-2" role="status"></span>
                        <i v-else class="bi bi-save me-2"></i>
                        {{ loading ? 'Updating...' : 'Save Changes' }}
                      </button>

                      <button
                          @click="cancelEdit"
                          type="button"
                          class="btn btn-outline-secondary me-2"
                      >
                        <i class="bi bi-x-circle me-2"></i>
                        Cancel
                      </button>

                      <button
                          @click="resetForm"
                          type="button"
                          class="btn btn-outline-warning"
                      >
                        <i class="bi bi-arrow-clockwise me-2"></i>
                        Reset Changes
                      </button>
                    </div>

                    <button
                        @click="toggleHistory"
                        type="button"
                        class="btn btn-outline-info"
                    >
                      <i class="bi bi-clock-history me-2"></i>
                      {{ showHistory ? 'Hide' : 'View' }} History
                    </button>
                  </div>
                </div>
              </div>

              <!-- Payment History -->
              <div v-if="showHistory" class="card mt-4">
                <div class="card-header bg-white">
                  <h5 class="mb-0">
                    <i class="bi bi-clock-history me-2"></i>
                    Payment History
                  </h5>
                </div>
                <div class="card-body">
                  <div class="table-responsive">
                    <table class="table table-hover">
                      <thead>
                      <tr>
                        <th>Payment Date</th>
                        <th>Reference</th>
                        <th>Method</th>
                        <th>Amount</th>
                        <th>Year</th>
                        <th>Status</th>
                        <th>Receipt</th>
                      </tr>
                      </thead>
                      <tbody>
                      <tr v-for="payment in payments" :key="payment.id">
                        <td>{{ formatDate(payment.paymentDate) }}</td>
                        <td>{{ payment.reference }}</td>
                        <td>{{ payment.method }}</td>
                        <td>ETB {{ formatCurrency(payment.amount) }}</td>
                        <td>{{ payment.year }}</td>
                        <td>
                              <span class="badge" :class="getStatusBadgeClass(payment.status)">
                                {{ payment.status }}
                              </span>
                        </td>
                        <td>{{ payment.receipt || '-' }}</td>
                      </tr>
                      <tr v-if="payments.length === 0">
                        <td colspan="7" class="text-center text-muted">No payment history available</td>
                      </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Footer -->
      <footer class="footer">
        <div class="container-fluid">
          <div class="row">
            <div class="col-sm-6">
              {{ currentYear }} © CBHI System
            </div>
            <div class="col-sm-6">
              <div class="text-sm-end">
                Ministry of Health - Ethiopia
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  </div>
  </body>
</template>

<script>
import NavigationBar from "@/components/Navigation.vue";
import { authUrl, executeJson } from "@/api";

export default {
  name: "MembersEdit",
  components: { NavigationBar },

  data() {
    return {
      // Loading states
      loading: false,
      initialLoading: true,
      showHistory: false,

      // Member data
      memberId: null,
      originalData: null,

      // Form data
      memberForm: {
        // Editable fields only
        title: '',
        fullName: '',
        grandfatherName: '',
        gender: '',
        dateOfBirth: '',
        nationalId: '',
        maritalStatus: '',
        occupation: '',
        monthlyIncome: 0,
        phoneNumber: '',
        emailAddress: '',
        address: '',

        // Location IDs (for dropdowns)
        regionId: '',
        zoneId: '',
        woredaId: '',
        kebeleId: '',

        // Read-only fields (not editable, just for display)
        cbhiNumber: '',
        status: ''
      },

      // Location data
      regions: [],
      zones: [],
      woredas: [],
      kebeles: [],

      // Member status - initialize with proper structure
      memberStatus: {
        isActive: false,
        daysUntilExpiry: 0,
        totalBeneficiaries: 0,
        totalPaidPremiums: 0,
        totalClaims: 0
      },

      // Payment history
      payments: [],

      // Validation
      errors: {},
      locationValidationError: false,

      // Alert state
      alert: {
        show: false,
        type: '',
        message: ''
      }
    };
  },

  computed: {
    currentYear() {
      return new Date().getFullYear();
    },

    maxDate() {
      return new Date().toISOString().split('T')[0];
    },

    statusBadgeClass() {
      if (!this.memberForm.status) return 'bg-secondary';
      return this.memberForm.status.toLowerCase() === 'active' ? 'bg-success' : 'bg-danger';
    },

    alertClass() {
      const classes = {
        success: 'alert-success',
        error: 'alert-danger',
        warning: 'alert-warning',
        info: 'alert-info'
      };
      return classes[this.alert.type] || 'alert-info';
    },

    hasChanges() {
      return JSON.stringify(this.memberForm) !== JSON.stringify(this.originalData?.memberForm);
    }
  },

  mounted() {
    this.memberId = this.$route.params.cbhi_number;
    if (this.memberId) {
      this.loadInitialData();
    } else {
      this.$router.push('/members');
    }
  },

  methods: {
    async loadInitialData() {
      await this.loadRegions();
      await this.loadMemberDetails();
    },

    async loadRegions() {
      const data = {
        "function": "getRegions"
      };

      try {
        const res = await executeJson(data, authUrl);
        if (res.data.success) {
          this.regions = res.data.data.regions || [];
        }
      } catch (e) {
        console.error("Failed to load regions:", e);
      }
    },

    async loadZones(regionId) {
      const data = {
        "function": "getZones",
        "regionId": regionId
      };

      try {
        const res = await executeJson(data, authUrl);
        if (res.data.success) {
          this.zones = res.data.data.zones || [];
        }
      } catch (e) {
        console.error("Failed to load zones:", e);
      }
    },

    async loadWoredas(zoneId) {
      const data = {
        "function": "getWoredas",
        "zoneId": zoneId
      };

      try {
        const res = await executeJson(data, authUrl);
        if (res.data.success) {
          this.woredas = res.data.data.woredas || [];
        }
      } catch (e) {
        console.error("Failed to load woredas:", e);
      }
    },

    async loadKebeles(woredaId) {
      const data = {
        "function": "getKebeles",
        "woredaId": woredaId
      };

      try {
        const res = await executeJson(data, authUrl);
        if (res.data.success) {
          this.kebeles = res.data.data.kebeles || [];
        }
      } catch (e) {
        console.error("Failed to load kebeles:", e);
      }
    },

    async onRegionChange() {
      this.memberForm.zoneId = '';
      this.memberForm.woredaId = '';
      this.memberForm.kebeleId = '';
      this.zones = [];
      this.woredas = [];
      this.kebeles = [];
      this.errors.zoneId = '';
      this.errors.woredaId = '';
      this.errors.kebeleId = '';

      if (this.memberForm.regionId) {
        await this.loadZones(this.memberForm.regionId);
      }
    },

    async onZoneChange() {
      this.memberForm.woredaId = '';
      this.memberForm.kebeleId = '';
      this.woredas = [];
      this.kebeles = [];
      this.errors.woredaId = '';
      this.errors.kebeleId = '';

      if (this.memberForm.zoneId) {
        await this.loadWoredas(this.memberForm.zoneId);
      }
    },

    async onWoredaChange() {
      this.memberForm.kebeleId = '';
      this.kebeles = [];
      this.errors.kebeleId = '';

      if (this.memberForm.woredaId) {
        await this.loadKebeles(this.memberForm.woredaId);
      }
    },

    calculateAge(dateOfBirth) {
      if (!dateOfBirth) return 0;
      const today = new Date();
      const birthDate = new Date(dateOfBirth);
      let age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();

      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
        age--;
      }

      return age;
    },

    findRegionIdByName(regionName) {
      if (!regionName || !this.regions.length) return '';
      const region = this.regions.find(r =>
          r.region_name.toLowerCase() === regionName.toLowerCase()
      );
      return region ? region.region_id : '';
    },

    findZoneIdByName(zoneName, regionId) {
      if (!zoneName || !this.zones.length) return '';
      const zone = this.zones.find(z =>
          z.zone_name.toLowerCase() === zoneName.toLowerCase() &&
          z.region_id === regionId
      );
      return zone ? zone.zone_id : '';
    },

    findWoredaIdByName(woredaName, zoneId) {
      if (!woredaName || !this.woredas.length) return '';
      const woreda = this.woredas.find(w =>
          w.woreda_name.toLowerCase() === woredaName.toLowerCase() &&
          w.zone_id === zoneId
      );
      return woreda ? woreda.woreda_id : '';
    },

    findKebeleIdByName(kebeleName, woredaId) {
      if (!kebeleName || !this.kebeles.length) return '';
      const kebele = this.kebeles.find(k =>
          k.kebele_name.toLowerCase() === kebeleName.toLowerCase() &&
          k.woreda_id === woredaId
      );
      return kebele ? kebele.kebele_id : '';
    },

    async loadMemberDetails() {
      this.initialLoading = true;

      try {
        const response = await executeJson({
          function: "getMemberDetails",
          cbhiNumber: this.memberId
        }, authUrl);

        console.log('API Response:', response);

        if (response.data && response.data.success) {
          const data = response.data.data;

          if (!data || !data.memberInfo) {
            throw new Error('Invalid response structure');
          }

          const memberInfo = data.memberInfo;

          // Extract location names from the response
          const locationNames = {
            region: memberInfo.location?.region || '',
            zone: memberInfo.location?.zone || '',
            woreda: memberInfo.location?.woreda || '',
            kebele: memberInfo.location?.kebele || ''
          };

          console.log('Location names from API:', locationNames);

          // First, find region ID by name
          let regionId = '';
          if (locationNames.region) {
            regionId = this.findRegionIdByName(locationNames.region);
            console.log('Found region ID:', regionId, 'for', locationNames.region);
          }

          // Load zones for this region
          if (regionId) {
            await this.loadZones(regionId);
          }

          // Find zone ID by name
          let zoneId = '';
          if (locationNames.zone && this.zones.length > 0) {
            zoneId = this.findZoneIdByName(locationNames.zone, regionId);
            console.log('Found zone ID:', zoneId, 'for', locationNames.zone);
          }

          // Load woredas for this zone
          if (zoneId) {
            await this.loadWoredas(zoneId);
          }

          // Find woreda ID by name
          let woredaId = '';
          if (locationNames.woreda && this.woredas.length > 0) {
            woredaId = this.findWoredaIdByName(locationNames.woreda, zoneId);
            console.log('Found woreda ID:', woredaId, 'for', locationNames.woreda);
          }

          // Load kebeles for this woreda
          if (woredaId) {
            await this.loadKebeles(woredaId);
          }

          // Find kebele ID by name
          let kebeleId = '';
          if (locationNames.kebele && this.kebeles.length > 0) {
            kebeleId = this.findKebeleIdByName(locationNames.kebele, woredaId);
            console.log('Found kebele ID:', kebeleId, 'for', locationNames.kebele);
          }

          // Map member information - only editable fields
          this.memberForm = {
            // Editable fields
            title: memberInfo.title || '',
            fullName: memberInfo.fullName || '',
            grandfatherName: memberInfo.grandfatherName || '',
            gender: memberInfo.gender || '',
            dateOfBirth: memberInfo.dateOfBirth || '',
            nationalId: memberInfo.nationalId || '',
            maritalStatus: memberInfo.maritalStatus || '',
            occupation: memberInfo.occupation || '',
            monthlyIncome: parseFloat(memberInfo.monthlyIncome) || 0,
            phoneNumber: memberInfo.phoneNumber || '',
            emailAddress: memberInfo.emailAddress || '',
            address: memberInfo.address || '',

            // Location IDs - use the found IDs
            regionId: regionId,
            zoneId: zoneId,
            woredaId: woredaId,
            kebeleId: kebeleId,

            // Read-only fields
            cbhiNumber: memberInfo.cbhiNumber || '',
            status: data.membershipStatus?.status || ''
          };

          console.log('Final memberForm location IDs:', {
            regionId: this.memberForm.regionId,
            zoneId: this.memberForm.zoneId,
            woredaId: this.memberForm.woredaId,
            kebeleId: this.memberForm.kebeleId
          });

          // Map member status with safe defaults
          if (data.membershipStatus) {
            this.memberStatus = {
              isActive: data.membershipStatus.isActive === true,
              daysUntilExpiry: data.membershipStatus.daysUntilExpiry || 0,
              totalBeneficiaries: data.householdInfo?.totalBeneficiaries || 0,
              totalPaidPremiums: parseFloat(data.financialSummary?.totalPaidThisYear) || 0,
              totalClaims: data.coverageUtilization?.totalClaimsThisYear || 0
            };
          }

          // Map payments from recentActivity.payments
          this.payments = data.recentActivity?.payments?.map(p => ({
            id: p.payment_id,
            paymentDate: p.payment_date,
            reference: p.payment_reference,
            method: p.method_name,
            amount: parseFloat(p.amount) || 0,
            year: p.payment_year,
            status: p.status,
            receipt: p.receipt_number
          })) || [];

          // Store original data for reset
          this.originalData = {
            memberForm: JSON.parse(JSON.stringify(this.memberForm))
          };

        } else {
          this.showAlert('error', response.data?.message || 'Failed to load member details');
          setTimeout(() => {
            this.$router.push('/members');
          }, 2000);
        }
      } catch (error) {
        console.error('Error loading member:', error);
        this.showAlert('error', 'Error loading member details: ' + error.message);
        setTimeout(() => {
          this.$router.push('/members');
        }, 2000);
      } finally {
        this.initialLoading = false;
      }
    },

    validateForm() {
      this.errors = {};
      this.locationValidationError = false;
      let isValid = true;

      // Validate required full name
      if (!this.memberForm.fullName || this.memberForm.fullName.trim() === '') {
        this.errors.fullName = 'Full name is required';
        isValid = false;
      }

      if (!this.memberForm.gender) {
        this.errors.gender = 'Gender is required';
        isValid = false;
      }

      if (!this.memberForm.dateOfBirth) {
        this.errors.dateOfBirth = 'Date of birth is required';
        isValid = false;
      }

      // Validate address
      if (!this.memberForm.address) {
        this.errors.address = 'Address is required';
        isValid = false;
      }

      // Validate email if provided
      if (this.memberForm.emailAddress && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.memberForm.emailAddress)) {
        this.errors.emailAddress = 'Invalid email format';
        isValid = false;
      }

      // Validate location fields - all must be filled
      if (!this.memberForm.regionId) {
        this.errors.regionId = 'Region is required';
        this.locationValidationError = true;
        isValid = false;
      }
      if (!this.memberForm.zoneId) {
        this.errors.zoneId = 'Zone is required';
        this.locationValidationError = true;
        isValid = false;
      }
      if (!this.memberForm.woredaId) {
        this.errors.woredaId = 'Woreda is required';
        this.locationValidationError = true;
        isValid = false;
      }
      if (!this.memberForm.kebeleId) {
        this.errors.kebeleId = 'Kebele is required';
        this.locationValidationError = true;
        isValid = false;
      }

      return isValid;
    },

    async updateMember() {
      if (!this.validateForm()) {
        this.showAlert('error', 'Please fix all errors before submitting. All location fields are required.');

        // Find first error field and scroll to it
        const firstErrorField = Object.keys(this.errors)[0];
        if (firstErrorField) {
          this.$nextTick(() => {
            const element = document.querySelector(`[v-model="memberForm.${firstErrorField}"]`) ||
                document.querySelector(`.is-invalid`);
            if (element) {
              element.scrollIntoView({ behavior: 'smooth', block: 'center' });
              if (element.focus) element.focus();
            }
          });
        }

        return;
      }

      this.loading = true;
      this.locationValidationError = false;

      try {
        const updateData = {
          function: "updateMember",
          memberId: this.memberId, // This is actually the CBHI number
          // Only send editable fields
          title: this.memberForm.title,
          fullName: this.memberForm.fullName,
          grandfatherName: this.memberForm.grandfatherName,
          gender: this.memberForm.gender,
          dateOfBirth: this.memberForm.dateOfBirth,
          nationalId: this.memberForm.nationalId,
          maritalStatus: this.memberForm.maritalStatus,
          occupation: this.memberForm.occupation,
          monthlyIncome: this.memberForm.monthlyIncome,
          phoneNumber: this.memberForm.phoneNumber,
          emailAddress: this.memberForm.emailAddress,
          address: this.memberForm.address,
          regionId: this.memberForm.regionId,
          zoneId: this.memberForm.zoneId,
          woredaId: this.memberForm.woredaId,
          kebeleId: this.memberForm.kebeleId
        };

        const response = await executeJson(updateData, authUrl);

        if (response.data.success) {
          this.showAlert('success', 'Member information updated successfully!');

          // Update original data
          this.originalData = {
            memberForm: JSON.parse(JSON.stringify(this.memberForm))
          };

          // Reload data after short delay
          setTimeout(() => {
            this.loadMemberDetails();
          }, 1500);
        } else {
          this.showAlert('error', response.data.message || 'Failed to update member');
        }
      } catch (error) {
        console.error('Update error:', error);
        this.showAlert('error', 'Error updating member information');
      } finally {
        this.loading = false;
      }
    },

    resetForm() {
      if (confirm('Are you sure you want to reset all changes?')) {
        this.memberForm = JSON.parse(JSON.stringify(this.originalData.memberForm));
        this.errors = {};
        this.locationValidationError = false;
        this.showAlert('info', 'Form has been reset');
      }
    },

    cancelEdit() {
      if (this.hasChanges) {
        if (confirm('You have unsaved changes. Are you sure you want to leave?')) {
          this.$router.push('/members');
        }
      } else {
        this.$router.push('/members');
      }
    },

    toggleHistory() {
      this.showHistory = !this.showHistory;
    },

    showAlert(type, message) {
      this.alert = {
        show: true,
        type,
        message
      };

      if (type === 'success' || type === 'info') {
        setTimeout(() => {
          this.clearAlert();
        }, 5000);
      }
    },

    clearAlert() {
      this.alert.show = false;
    },

    formatDate(dateString) {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    },

    formatCurrency(amount) {
      return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(amount);
    },

    getStatusBadgeClass(status) {
      const classes = {
        'completed': 'bg-success',
        'pending': 'bg-warning',
        'failed': 'bg-danger',
        'reversed': 'bg-secondary'
      };
      return classes[status?.toLowerCase()] || 'bg-primary';
    }
  },

  beforeRouteLeave(to, from, next) {
    if (this.hasChanges) {
      if (confirm('You have unsaved changes. Do you really want to leave?')) {
        next();
      } else {
        next(false);
      }
    } else {
      next();
    }
  }
};
</script>

<style scoped>
/* Main Layout */
.main-content {
  margin-left: 250px;
  min-height: 100vh;
  background-color: #f5f7fa;
}

.page-content {
  padding: 20px;
}

/* Card Styling */
.card {
  border: none;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.08);
  margin-bottom: 24px;
}

.card-header {
  border-bottom: 1px solid #e9ecef;
  padding: 20px;
}

.card-body {
  padding: 24px;
}

.card-footer {
  border-top: 1px solid #e9ecef;
  padding: 16px 24px;
}

/* Section Headers */
.section-header {
  padding: 12px 16px;
  background-color: #f8f9fa;
  border-left: 4px solid #0d6efd;
  border-radius: 4px;
}

/* Form Controls */
.form-control-plaintext {
  padding-left: 0;
  background-color: transparent;
  border: none;
  color: #495057;
}

.form-control, .form-select {
  border: 1px solid #ced4da;
  border-radius: 6px;
  padding: 10px 12px;
  font-size: 14px;
  transition: all 0.3s ease;
  background-color: #fff;
}

.form-control:focus, .form-select:focus {
  border-color: #0d6efd;
  box-shadow: 0 0 0 0.2rem rgba(13, 110, 253, 0.15);
  background-color: #fff;
  outline: none;
}

.form-control:hover:not(:focus), .form-select:hover:not(:focus) {
  border-color: #86b7fe;
}

.form-label {
  font-weight: 600;
  color: #212529;
  margin-bottom: 8px;
  font-size: 14px;
}

.input-group {
  position: relative;
}

.input-group-text {
  background-color: #f8f9fa;
  border: 1px solid #ced4da;
  border-right: none;
  color: #6c757d;
  padding: 10px 12px;
}

.input-group .form-control {
  border-left: none;
}

.input-group .form-control:focus {
  border-left: none;
}

/* Differentiate editable fields */
.form-control:not([readonly]) {
  background-color: #ffffff;
  border: 2px solid #e3e7ef;
}

.form-control:not([readonly]):focus {
  background-color: #f8fafe;
  border-color: #0d6efd;
}

.form-select:not([disabled]) {
  background-color: #ffffff;
  border: 2px solid #e3e7ef;
  cursor: pointer;
}

.form-select:not([disabled]):focus {
  background-color: #f8fafe;
  border-color: #0d6efd;
}

/* Buttons */
.btn {
  font-weight: 500;
  padding: 8px 16px;
  border-radius: 4px;
  transition: all 0.3s;
}

.btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.btn-primary {
  background-color: #0d6efd;
  border-color: #0d6efd;
}

.btn-primary:hover {
  background-color: #0b5ed7;
  border-color: #0a58ca;
}

/* Alerts */
.alert {
  border: none;
  border-radius: 6px;
  padding: 12px 20px;
}

.alert-info {
  background-color: #cff4fc;
  color: #055160;
}

.alert-warning {
  background-color: #fff3cd;
  border-color: #ffeeba;
  color: #856404;
}

/* Loading Spinner */
.spinner-border {
  width: 1.5rem;
  height: 1.5rem;
}

.spinner-border-sm {
  width: 1rem;
  height: 1rem;
}

/* Table */
.table {
  margin-bottom: 0;
}

.table thead th {
  background-color: #f8f9fa;
  border-bottom: 2px solid #dee2e6;
  font-weight: 600;
  text-transform: uppercase;
  font-size: 0.875rem;
  padding: 12px;
}

.table tbody td {
  padding: 12px;
  vertical-align: middle;
}

/* Badges */
.badge {
  padding: 6px 12px;
  font-weight: 500;
  font-size: 0.875rem;
}

/* Footer */
.footer {
  background-color: #fff;
  padding: 20px;
  border-top: 1px solid #e9ecef;
  margin-top: auto;
}

/* Responsive */
@media (max-width: 768px) {
  .main-content {
    margin-left: 0;
  }
}

/* Validation */
.is-invalid {
  border-color: #dc3545 !important;
}

.invalid-feedback {
  display: block;
  margin-top: 0.25rem;
  font-size: 0.875em;
  color: #dc3545;
}

/* Transitions */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>